import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { useTranslation } from "react-i18next";
import FileDownload from "js-file-download";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import TimerIcon from "@material-ui/icons/Timer";
import VisibilityIcon from "@material-ui/icons/Visibility";
import GetAppIcon from "@material-ui/icons/GetApp";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PublishIcon from "@material-ui/icons/Publish";
import AssessmentIcon from "@material-ui/icons/Assessment";
import SearchIcon from "@material-ui/icons/Search";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { Pagination } from "@material-ui/lab";
import managerTestApi from "api/managerTest";
import { useConfirm } from "material-ui-confirm";
import axios, { AxiosResponse } from "axios";
import { BASE_URL, TOKEN } from "constants/config";
import { ListExam, ListTest, ListTestByCodeExamResponse } from "models/managerTest";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { dateTimeToFormatHHMM, dateTimeToFormatYYYY } from "utils/dateTimeFormat";
import { secondsToHms } from "utils/timeConvert";
import clsx from "clsx";

export interface ListTestByCodeExamProps {
  hideHeader?: boolean;
  mode?: "all" | "submitted";
}

export default function ListTestByCodeExam(props?: ListTestByCodeExamProps) {
  const confirm = useConfirm();
  const useStyles = makeStyles((theme: Theme) => ({
    container: {
      paddingTop: 28,
      paddingBottom: 48,
      position: "relative",
      [theme.breakpoints?.down("sm")]: {
        paddingTop: 12,
        paddingBottom: 16,
        paddingLeft: "0px !important",
        paddingRight: "0px !important",
      },
    },
    backButton: {
      marginBottom: 20,
      color: "#475569",
      fontWeight: 700,
      fontSize: "0.875rem",
      textTransform: "none",
      borderRadius: 10,
      padding: "6px 14px",
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#F1F5F9",
        color: "#0F172A",
        transform: "translateX(-2px)",
      },
    },
    headerBanner: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      borderRadius: 24,
      padding: "32px 36px",
      color: "#FFFFFF",
      marginBottom: 28,
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.25)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 20,
      [theme.breakpoints?.down("sm")]: {
        padding: "20px 18px",
        borderRadius: 16,
      },
    },
    headerGlow: {
      position: "absolute",
      top: "-40%",
      right: "-10%",
      width: 350,
      height: 350,
      background: "radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, rgba(99, 102, 241, 0) 70%)",
      pointerEvents: "none",
    },
    headerBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      backdropFilter: "blur(8px)",
      padding: "4px 14px",
      borderRadius: 20,
      fontSize: "0.75rem",
      fontWeight: 800,
      color: "#818CF8",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: 12,
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },
    headerTitle: {
      fontSize: "1.75rem",
      fontWeight: 900,
      color: "#FFFFFF",
      marginBottom: 12,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "1.25rem",
      },
    },
    headerSubtitle: {
      fontSize: "0.95rem",
      color: "#94A3B8",
      fontWeight: 500,
    },
    bannerActions: {
      display: "flex",
      gap: 12,
      flexWrap: "wrap",
      zIndex: 2,
    },
    btnExportAll: {
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(8px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      color: "#FFFFFF",
      borderRadius: 12,
      padding: "10px 20px",
      fontSize: "0.875rem",
      fontWeight: 700,
      textTransform: "none",
      display: "flex",
      alignItems: "center",
      gap: 8,
      transition: "all 0.2s ease",
      "&:hover": {
        background: "#FFFFFF",
        color: "#0F172A",
        transform: "translateY(-1px)",
      },
    },
    btnImportAll: {
      background: "linear-gradient(135deg, #F43F5E 0%, #E11D48 100%)",
      color: "#FFFFFF",
      borderRadius: 12,
      padding: "10px 20px",
      fontSize: "0.875rem",
      fontWeight: 700,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      cursor: "pointer",
      boxShadow: "0 4px 14px rgba(225, 29, 72, 0.3)",
      transition: "all 0.2s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 6px 20px rgba(225, 29, 72, 0.4)",
      },
    },
    tableCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      border: "1px solid #E2E8F0",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.03)",
      overflow: "hidden",
      marginBottom: 20,
    },
    tableHeaderRow: {
      backgroundColor: "#F8FAFC",
      borderBottom: "2px solid #E2E8F0",
    },
    tableHeaderCell: {
      fontWeight: 800,
      color: "#475569",
      textTransform: "uppercase",
      fontSize: "0.75rem",
      letterSpacing: "0.05em",
      padding: "18px 16px",
      borderBottom: "2px solid #E2E8F0",
      top: "0 !important",
    },
    tableRow: {
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: "#F8FAFC",
      },
    },
    tableCell: {
      padding: "16px 16px",
      fontSize: "0.875rem",
      color: "#334155",
      fontWeight: 500,
      borderBottom: "1px solid #F1F5F9",
    },
    studentName: {
      fontWeight: 700,
      color: "#0F172A",
    },
    staffId: {
      fontFamily: "monospace",
      backgroundColor: "#F1F5F9",
      color: "#475569",
      padding: "2px 8px",
      borderRadius: 6,
      fontSize: "0.75rem",
      fontWeight: 700,
    },
    userEmail: {
      color: "#64748B",
      fontSize: "0.85rem",
      fontWeight: 500,
    },
    scoreBadge: {
      display: "inline-flex",
      alignItems: "center",
      backgroundColor: "#ECFDF5",
      color: "#047857",
      padding: "4px 12px",
      borderRadius: 99,
      fontWeight: 800,
      fontSize: "0.825rem",
    },
    durationBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      color: "#64748B",
      fontSize: "0.825rem",
      fontWeight: 600,
    },
    actionGroup: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    btnActionDetail: {
      color: "#4F46E5",
      backgroundColor: "#EEF2FF",
      borderRadius: 10,
      padding: "6px 14px",
      fontSize: "0.75rem",
      fontWeight: 700,
      textTransform: "none",
      display: "flex",
      alignItems: "center",
      gap: 6,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#4F46E5",
        color: "#FFFFFF",
        boxShadow: "0 4px 12px rgba(79, 70, 229, 0.2)",
      },
    },
    btnActionExport: {
      color: "#0284C7",
      backgroundColor: "#F0F9FF",
      borderRadius: 10,
      padding: "6px 14px",
      fontSize: "0.75rem",
      fontWeight: 700,
      textTransform: "none",
      display: "flex",
      alignItems: "center",
      gap: 6,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#0284C7",
        color: "#FFFFFF",
        boxShadow: "0 4px 12px rgba(2, 132, 199, 0.2)",
      },
    },
    btnActionEdit: {
      color: "#D97706",
      backgroundColor: "#FFFBEB",
      borderRadius: 10,
      padding: "6px 14px",
      fontSize: "0.75rem",
      fontWeight: 700,
      textTransform: "none",
      display: "flex",
      alignItems: "center",
      gap: 6,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#D97706",
        color: "#FFFFFF",
        boxShadow: "0 4px 12px rgba(217, 119, 6, 0.2)",
      },
    },
    btnDelete: {
      color: "#EF4444",
      backgroundColor: "#FEF2F2",
      borderRadius: 10,
      padding: "6px 14px",
      fontSize: "0.75rem",
      fontWeight: 700,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#EF4444",
        color: "#FFFFFF",
        boxShadow: "0 4px 12px rgba(239, 68, 68, 0.2)",
      },
    },
    paginationContainer: {
      marginTop: 24,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#FFFFFF",
      padding: "16px 24px",
      borderRadius: 16,
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.02)",
      [theme.breakpoints?.down("sm")]: {
        flexDirection: "column",
        gap: 12,
        padding: "16px 12px",
        borderRadius: 12,
      },
    },
    emptyStateCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      border: "1px solid #E2E8F0",
      padding: "48px 24px",
      textAlign: "center",
      color: "#64748B",
    },
    filterToolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 20,
      gap: 16,
      flexWrap: "wrap",
      position: "sticky",
      top: 76,
      zIndex: 100,
      backgroundColor: "rgba(255, 255, 255, 0.85)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      padding: "14px 20px",
      borderRadius: 16,
      boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.05)",
      border: "1px solid rgba(226, 232, 240, 0.8)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      [theme.breakpoints?.down("sm")]: {
        top: 68,
        padding: "10px 12px",
        borderRadius: 12,
      },
    },
    stickyActive: {
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05) !important",
      border: "1px solid rgba(15, 23, 42, 0.08) !important",
      backgroundColor: "#FFFFFF !important",
    },
    searchField: {
      backgroundColor: "#FFFFFF",
      borderRadius: 12,
      width: 320,
      [theme.breakpoints?.down("sm")]: {
        width: "100%",
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        height: 40,
      },
    },
    sortControlGroup: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      [theme.breakpoints?.down("sm")]: {
        width: "100%",
        flexWrap: "wrap",
        gap: 8,
      },
    },
    mobileSortFormControl: {
      minWidth: 140,
      flex: "1 1 auto",
      backgroundColor: "#FFFFFF",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        height: 40,
      },
    },
    btnExportExcel: {
      height: 40,
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 700,
      boxSizing: "border-box",
      padding: "0 16px",
      [theme.breakpoints?.down("sm")]: {
        width: "100%",
      },
    },
    btnSortDirection: {
      height: 40,
      color: "#475569",
      backgroundColor: "#FFFFFF",
      border: "1px solid #E2E8F0",
      borderRadius: 12,
      padding: "8px 12px",
      textTransform: "none",
      fontWeight: 700,
      boxSizing: "border-box",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#F1F5F9",
      },
    },
    mobileCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 16,
      border: "1px solid #E2E8F0",
      padding: 16,
      marginBottom: 12,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
    },
    mobileCardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12,
    },
    mobileCardBody: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 14,
      backgroundColor: "#F8FAFC",
      padding: "10px 12px",
      borderRadius: 10,
    },
    mobileCardActions: {
      display: "flex",
      gap: 8,
      justifyContent: "flex-end",
    },
  }));

  const token = localStorage.getItem(TOKEN);
  const { t } = useTranslation();
  const classes = useStyles();
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [detailExam, setDetailExam] = useState<ListExam>();
  const [listTest, setListTest] = useState<ListTestByCodeExamResponse<ListTest>>();
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [filter, setFilter] = useState({
    page: 0,
    size: 10,
    search: "",
    sortBy: "id",
    sortDir: "DESC" as "ASC" | "DESC",
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const scrollContainer = containerRef.current?.closest("main");
    if (!scrollContainer) return;

    const handleScroll = () => {
      setIsSticky(scrollContainer.scrollTop > 180);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let targetParent: HTMLElement | null = null;
    let originalOverflow = "";

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent) {
        if (
          parent.className &&
          typeof parent.className === "string" &&
          parent.className.includes("container")
        ) {
          targetParent = parent;
          originalOverflow = parent.style.overflow;
          parent.style.overflow = "visible";
          break;
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      if (targetParent) {
        targetParent.style.overflow = originalOverflow;
      }
    };
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilter((prev) => ({ ...prev, search: searchInput, page: 0 }));
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    (async () => {
      try {
        const res: ListExam = await managerTestApi.getDetailExam(Number(params.id));
        setDetailExam(res);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [params.id]);

  const handleFetchListTest = useCallback(async () => {
    try {
      setLoading(true);
      const apiMethod =
        props?.mode === "all"
          ? managerTestApi.getAllCandidatesByExamId
          : managerTestApi.getListTest;

      const res: ListTestByCodeExamResponse<ListTest> = await apiMethod(
        Number(params.id),
        filter
      );
      if (res.responseCode === "00") {
        setListTest(res);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [params.id, filter, props?.mode]);

  useEffect(() => {
    handleFetchListTest();
  }, [filter]);

  const handleChangePage = (page: number) => {
    setFilter({ ...filter, page: page - 1 });
  };

  const handleDeleteUser = async (test: any) => {
    confirm({
      title: (
        <Typography variant="h6" style={{ fontWeight: 800, color: "#0F172A" }}>
          Xác nhận xóa thí sinh
        </Typography>
      ),
      description: (
        <Typography variant="body2" style={{ color: "#475569" }}>
          Bạn có chắc chắn muốn xóa thí sinh <strong>{test?.student?.name}</strong> ({test?.student?.staffId || "N/A"}) khỏi bài thi này?
        </Typography>
      ),
      confirmationText: "Đồng ý",
      cancellationText: "Hủy bỏ",
    }).then(async () => {
      try {
        const studentId = test?.student?.id;
        if (!studentId) return;
        const value = {
          examID: params.id,
          userIdList: [studentId],
        };
        const res: any = await managerTestApi.deleteUserFromExam(value);
        if (res.responseCode === "00") {
          toast.success("Xóa thí sinh khỏi bài thi thành công");
          handleFetchListTest();
        }
      } catch (error) {}
    });
  };

  const handleRequestSort = (property: string) => {
    const isAsc = filter.sortBy === property && filter.sortDir === "ASC";
    setFilter((prev) => ({
      ...prev,
      sortBy: property,
      sortDir: isAsc ? "DESC" : "ASC",
      page: 0,
    }));
  };

  const handleImport = async (event: any) => {
    const fileExcel = event?.target?.files?.[0];
    if (!fileExcel) return;
    const formData = new FormData();
    formData.append("file", fileExcel);
    try {
      const res: AxiosResponse = await axios({
        method: "POST",
        url: `${BASE_URL}/submissions/upload`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      });
      if (res.data.responseCode === "00") {
        toast.success(`${t("managerTest.nhapdulieuthanhcong")}`);
        handleFetchListTest();
      } else {
        toast.error(`${t("managerTest.nhapdulieuthatbai")}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportExam = async (linkApi: any, name?: string) => {
    if (!linkApi) return;
    const token = localStorage.getItem(TOKEN);
    try {
      let res: AxiosResponse;
      try {
        res = await axios({
          method: "GET",
          url: linkApi,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "blob",
          },
          responseType: "arraybuffer",
        });
      } catch (err: any) {
        if (err?.response?.status === 404 && linkApi.includes("exam-all-candidates")) {
          const fallbackUrl = linkApi.replace("exam-all-candidates", "submissions");
          res = await axios({
            method: "GET",
            url: fallbackUrl,
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "blob",
            },
            responseType: "arraybuffer",
          });
        } else {
          throw err;
        }
      }
      if (res.data.responseDescription) {
        toast.error(t("file.xuatthatbai"));
        return;
      }
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      FileDownload(blob, `${name}.xlsx`);
      toast.success(t("file.xuatthanhcong"));
    } catch (error) {
      toast.error(t("file.xuatthatbai"));
    }
  };

  const examTitle = detailExam?.exam?.examTitle || "Danh sách bài làm";
  const startDateStr = detailExam?.exam?.startDate
    ? `${dateTimeToFormatHHMM(detailExam.exam.startDate)} ${dateTimeToFormatYYYY(detailExam.exam.startDate)}`
    : "";
  const isTyping = detailExam?.exam?.type === "TYPING";

  const WrapperComponent = props?.hideHeader ? Box : Container;
  const wrapperProps = props?.hideHeader ? {} : { maxWidth: "lg" as const, className: classes.container };

  return (
    <div ref={containerRef}>
      <WrapperComponent {...(wrapperProps as any)}>
      {!props?.hideHeader && (
        <>
          <Button
            startIcon={<ArrowBackIcon />}
            className={classes.backButton}
            onClick={() => navigate(-1)}
          >
            Quay lại
          </Button>

          <Box className={classes.headerBanner}>
            <Box className={classes.headerGlow} />
            <Box>
              <Box className={classes.headerBadge}>
                <AssessmentIcon style={{ fontSize: 16 }} />
                DANH SÁCH BÀI LÀM
              </Box>
              <Typography className={classes.headerTitle}>{examTitle}</Typography>
              <Typography className={classes.headerSubtitle}>
                Thời gian bắt đầu: {startDateStr} • Thể loại: {isTyping ? "Tự luận" : "Trắc nghiệm"}
              </Typography>
            </Box>

            <Box className={classes.bannerActions}>
              {isTyping ? (
                <>
                  <Button
                    className={classes.btnExportAll}
                    onClick={() =>
                      handleExportExam(
                        `${BASE_URL}/submissions/download/${params.id}`,
                        `Danh-sach-bai-thi-${params.id}`
                      )
                    }
                    startIcon={<GetAppIcon />}
                  >
                    Xuất tất cả
                  </Button>
                  <label htmlFor="contained-button-file" className={classes.btnImportAll}>
                    <PublishIcon />
                    Nhập tất cả
                  </label>
                  <input
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8"
                    id="contained-button-file"
                    multiple
                    type="file"
                    name="image"
                    onChange={handleImport}
                    style={{ display: "none" }}
                  />
                </>
              ) : (
                <Button
                  className={classes.btnExportAll}
                  onClick={() =>
                    handleExportExam(
                      `${BASE_URL}/submissions/download/${params.id}`,
                      `Danh-sach-bai-thi-${params.id}`
                    )
                  }
                  startIcon={<GetAppIcon />}
                >
                  Xuất file excel
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* Filter Toolbar (Search & Mobile Sort Selector) */}
      <Box className={clsx(classes.filterToolbar, isSticky && classes.stickyActive)}>
        <TextField
          variant="outlined"
          placeholder="Tìm tên thí sinh, mã nhân viên..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className={classes.searchField}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
        />

        <Box className={classes.sortControlGroup}>
          <FormControl variant="outlined" size="small" className={classes.mobileSortFormControl}>
            <InputLabel id="mobile-sort-label">Sắp xếp theo</InputLabel>
            <Select
              labelId="mobile-sort-label"
              value={filter.sortBy}
              onChange={(e) => setFilter((prev) => ({ ...prev, sortBy: e.target.value as string, page: 0 }))}
              label="Sắp xếp theo"
            >
              <MenuItem value="id">STT mặc định</MenuItem>
              <MenuItem value="studentName">Thí sinh</MenuItem>
              <MenuItem value="staffId">Mã nhân viên</MenuItem>
              <MenuItem value="point">Điểm số</MenuItem>
              <MenuItem value="duration">Thời gian làm</MenuItem>
            </Select>
          </FormControl>
          <Button
            className={classes.btnSortDirection}
            onClick={() => setFilter((prev) => ({ ...prev, sortDir: prev.sortDir === "ASC" ? "DESC" : "ASC", page: 0 }))}
            startIcon={filter.sortDir === "ASC" ? <ArrowUpwardIcon style={{ fontSize: 16 }} /> : <ArrowDownwardIcon style={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
          >
            {filter.sortDir === "ASC" ? "Tăng dần" : "Giảm dần"}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            className={classes.btnExportExcel}
            startIcon={<GetAppIcon />}
            onClick={() =>
              handleExportExam(
                props?.mode === "all"
                  ? `${BASE_URL}/exam-all-candidates/download/${params.id}`
                  : `${BASE_URL}/submissions/download/${params.id}`,
                props?.mode === "all"
                  ? `Danh-sach-tat-ca-thi-sinh-${params.id}`
                  : `Danh-sach-bai-thi-${params.id}`
              )
            }
          >
            Xuất Excel
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress style={{ color: "#4F46E5" }} size={50} />
        </Box>
      ) : listTest && listTest?.submissionDetails?.content?.length > 0 ? (
        <>
          {isMobile ? (
            <Box>
              {listTest.submissionDetails.content.map((test, index) => (
                <Box
                  key={test?.id}
                  className={classes.mobileCard}
                  onClick={() =>
                    navigate(`/admin/manager-test/exam/list-test/detail/${test.id}`, {
                      state: {
                        examId: test?.examId,
                      },
                    })
                  }
                  style={{ cursor: "pointer" }}
                >
                  <Box className={classes.mobileCardHeader}>
                    <Box>
                      <Typography className={classes.studentName}>
                        {listTest.submissionDetails.size * listTest.submissionDetails.number + index + 1}. {test?.student?.name}
                      </Typography>
                      {test?.student?.email && (
                        <Typography className={classes.userEmail} style={{ fontSize: "0.75rem" }}>
                          {test.student.email}
                        </Typography>
                      )}
                    </Box>
                    <span className={classes.staffId}>{test?.student?.staffId || "N/A"}</span>
                  </Box>

                  <Box className={classes.mobileCardBody}>
                    <span className={classes.scoreBadge}>{test?.point} điểm</span>
                    <Box className={classes.durationBadge}>
                      <TimerIcon style={{ fontSize: 16 }} />
                      {secondsToHms(test?.duration)}
                    </Box>
                  </Box>

                  {isTyping && (
                    <Box className={classes.mobileCardActions}>
                      <Button
                        className={classes.btnActionExport}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportExam(
                            `${BASE_URL}/submission/download/${test.id}`,
                            `FILE-${test?.student?.name}`
                          );
                        }}
                        startIcon={<GetAppIcon style={{ fontSize: 14 }} />}
                      >
                        Xuất file
                      </Button>
                      <Button
                        className={classes.btnActionEdit}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/manager-test/exam/list-test/detail/${test.id}`, {
                            state: {
                              examId: test?.examId,
                              editPoint: true,
                            },
                          });
                        }}
                        startIcon={<EditIcon style={{ fontSize: 14 }} />}
                      >
                        Sửa điểm
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} className={classes.tableCard} elevation={0}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow className={classes.tableHeaderRow}>
                    <TableCell align="center" className={classes.tableHeaderCell} style={{ width: 80 }}>
                      STT
                    </TableCell>
                    <TableCell align="left" className={classes.tableHeaderCell} style={{ minWidth: 260 }}>
                      <TableSortLabel
                        active={filter.sortBy === "studentName"}
                        direction={filter.sortBy === "studentName" ? (filter.sortDir.toLowerCase() as "asc" | "desc") : "asc"}
                        onClick={() => handleRequestSort("studentName")}
                      >
                        Thí sinh
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell} style={{ width: 160 }}>
                      <TableSortLabel
                        active={filter.sortBy === "staffId"}
                        direction={filter.sortBy === "staffId" ? (filter.sortDir.toLowerCase() as "asc" | "desc") : "asc"}
                        onClick={() => handleRequestSort("staffId")}
                      >
                        Mã nhân viên
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell} style={{ width: 150 }}>
                      <TableSortLabel
                        active={filter.sortBy === "point"}
                        direction={filter.sortBy === "point" ? (filter.sortDir.toLowerCase() as "asc" | "desc") : "asc"}
                        onClick={() => handleRequestSort("point")}
                      >
                        Điểm số
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell} style={{ width: 180 }}>
                      <TableSortLabel
                        active={filter.sortBy === "duration"}
                        direction={filter.sortBy === "duration" ? (filter.sortDir.toLowerCase() as "asc" | "desc") : "asc"}
                        onClick={() => handleRequestSort("duration")}
                      >
                        Thời gian làm
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell} style={{ width: 260 }}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listTest.submissionDetails.content.map((test, index) => (
                    <TableRow key={test?.id} className={classes.tableRow}>
                      <TableCell align="center" className={classes.tableCell}>
                        {listTest.submissionDetails.size * listTest.submissionDetails.number + index + 1}
                      </TableCell>
                      <TableCell align="left" className={classes.tableCell}>
                        <Box display="flex" flexDirection="column">
                          <span className={classes.studentName}>{test?.student?.name}</span>
                          {(test?.student?.email || test?.student?.username) && (
                            <span className={classes.userEmail} style={{ marginTop: 2 }}>
                              {test?.student?.email || test?.student?.username}
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center" className={classes.tableCell}>
                        <span className={classes.staffId}>{test?.student?.staffId || "N/A"}</span>
                      </TableCell>
                      <TableCell align="center" className={classes.tableCell}>
                        {(test as any)?.isSubmitted !== false && test?.point != null ? (
                          <span className={classes.scoreBadge}>{test?.point} điểm</span>
                        ) : (
                          <span style={{ backgroundColor: "#F1F5F9", color: "#64748B", padding: "4px 12px", borderRadius: 99, fontWeight: 700, fontSize: "0.825rem" }}>
                            Chưa làm bài
                          </span>
                        )}
                      </TableCell>
                      <TableCell align="center" className={classes.tableCell}>
                        {(test as any)?.isSubmitted !== false && test?.duration != null ? (
                          <Box className={classes.durationBadge}>
                            <TimerIcon style={{ fontSize: 16 }} />
                            {secondsToHms(test?.duration)}
                          </Box>
                        ) : (
                          <span style={{ color: "#94A3B8", fontWeight: 500 }}>--</span>
                        )}
                      </TableCell>
                      <TableCell align="center" className={classes.tableCell}>
                        <Box className={classes.actionGroup}>
                          {(test as any)?.isSubmitted !== false && (test?.id || (test as any)?.submissionId) && (
                            <Button
                              className={classes.btnActionDetail}
                              onClick={() =>
                                navigate(`/admin/manager-test/exam/list-test/detail/${(test as any).submissionId || test.id}`, {
                                  state: {
                                    examId: test?.examId,
                                  },
                                })
                              }
                              startIcon={<VisibilityIcon style={{ fontSize: 14 }} />}
                            >
                              Chi tiết
                            </Button>
                          )}

                          {isTyping && (
                            <>
                              <Button
                                className={classes.btnActionExport}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExportExam(
                                    `${BASE_URL}/submission/download/${test.id}`,
                                    `FILE-${test?.student?.name}`
                                  );
                                }}
                                startIcon={<GetAppIcon style={{ fontSize: 14 }} />}
                              >
                                Xuất file
                              </Button>
                              <Button
                                className={classes.btnActionEdit}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/admin/manager-test/exam/list-test/detail/${test.id}`, {
                                    state: {
                                      examId: test?.examId,
                                      editPoint: true,
                                    },
                                  });
                                }}
                                startIcon={<EditIcon style={{ fontSize: 14 }} />}
                              >
                                Sửa điểm
                              </Button>
                            </>
                          )}

                          <Button
                            className={classes.btnDelete}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUser(test);
                            }}
                            startIcon={<DeleteOutlineIcon style={{ fontSize: 14 }} />}
                          >
                            Xóa
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {listTest.submissionDetails.totalPages > 1 && (
            <Box className={classes.paginationContainer}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#475569" }}>
                Tổng số bài thi: {listTest.submissionDetails.totalElements}
              </Typography>
              <Pagination
                count={Math.ceil(
                  listTest.submissionDetails.totalElements / listTest.submissionDetails.size
                )}
                page={listTest.submissionDetails.number + 1}
                variant="outlined"
                color="primary"
                shape="rounded"
                size={isMobile ? "small" : "medium"}
                siblingCount={isMobile ? 0 : 1}
                onChange={(e, page) => handleChangePage(page)}
              />
            </Box>
          )}
        </>
      ) : (
        <Paper className={classes.emptyStateCard} elevation={0}>
          <AssessmentIcon style={{ fontSize: 48, color: "#94A3B8", marginBottom: 12 }} />
          <Typography variant="h6" style={{ fontWeight: 700, color: "#475569", marginBottom: 6 }}>
            {filter.search ? "Không tìm thấy kết quả" : t("managerTest.hienchuacobaithi")}
          </Typography>
          <Typography variant="body2">
            {filter.search
              ? "Hãy thử tìm kiếm với từ khóa khác hoặc xóa bộ lọc."
              : "Hệ thống chưa ghi nhận lượt làm bài thi nào cho mã đề này."}
          </Typography>
        </Paper>
      )}
      </WrapperComponent>
    </div>
  );
}
